import { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { usePageTitle } from '../../hooks/usePageTitle'
import { useForm } from 'react-hook-form'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Lock } from 'lucide-react'
import toast from 'react-hot-toast'
import { useAuthStore } from '../../store/auth'
import { fullName as formatFullName } from '../../lib/utils'
import { authApi } from '../../api/auth'
import { companySettingsApi } from '../../api/settings'
import { DropdownSelect } from '../../components/ui/DropdownSelect'

const TIMEZONE_OPTIONS = [
  { value: 'Europe/Tirane', label: 'Europe/Tirane (UTC+1/+2, Albania)' },
  { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
]

/**
 * Tenant /settings/general now hosts two cards:
 *
 *  1. Company General — name, logo, website, timezone. Persisted via
 *     PATCH /companies/:cid/settings/general (UpdateCompanySettingsDto,
 *     which deliberately excludes isActive — see Notion #22). Editable
 *     by COMPANY_OWNER + ADMINISTRATOR per the controller's @Roles.
 *
 *  2. My Account — current-user info + Change Password (was the entire
 *     contents of this screen before #114; preserved as a sub-card so
 *     /settings/general still shows account fields).
 */
export function GeneralSettings() {
  usePageTitle('General Settings')
  return (
    <div className="space-y-6 max-w-lg">
      <h2 className="text-base font-semibold text-gray-900">General Settings</h2>
      <CompanyGeneralCard />
      <CompanyPreferencesCard />
      <MyAccountCard />
      <ChangePasswordCard />
    </div>
  )
}

const RETENTION_OPTIONS = [
  { value: '6', label: '6 months' },
  { value: '9', label: '9 months' },
  { value: '12', label: '12 months (1 year)' },
  { value: '24', label: '24 months (2 years)' },
]

function CompanyPreferencesCard() {
  const { companyId } = useParams<{ companyId: string }>()
  const qc = useQueryClient()
  const { user } = useAuthStore()
  const canEdit = user?.role !== 'sales_user' // company owner + administrator

  const { data } = useQuery({
    queryKey: ['company-general', companyId],
    queryFn: () => companySettingsApi.getGeneral(companyId!).then(r => r.data),
    enabled: !!companyId,
  })

  const mutation = useMutation({
    mutationFn: (show: boolean) => companySettingsApi.updatePreferences(companyId!, { showWhatsappInTimeline: show }),
    onSuccess: () => {
      toast.success('Preference saved')
      qc.invalidateQueries({ queryKey: ['company-general', companyId] })
    },
    onError: () => toast.error('Failed to save preference'),
  })

  const retentionMutation = useMutation({
    mutationFn: (months: number) => companySettingsApi.updatePreferences(companyId!, { recordingRetentionMonths: months }),
    onSuccess: () => {
      toast.success('Retention period saved')
      qc.invalidateQueries({ queryKey: ['company-general', companyId] })
    },
    onError: () => toast.error('Failed to save retention period'),
  })

  if (!canEdit) return null
  return (
    <div className="card p-4 space-y-4">
      <p className="text-sm font-medium text-gray-700">Preferences</p>
      <div className="space-y-1">
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            className="w-3.5 h-3.5"
            checked={!!data?.showWhatsappInTimeline}
            disabled={mutation.isPending}
            onChange={e => mutation.mutate(e.target.checked)}
          />
          <span>Show WhatsApp messages in lead/deal activity timeline</span>
        </label>
        <p className="text-xs text-gray-500">Off by default — WhatsApp chats stay in the WhatsApp page. Enable to also log them in the timeline (can make it long).</p>
      </div>
      <div className="space-y-1">
        <label className="label">Call recording retention</label>
        <div className="w-56">
          <DropdownSelect
            value={String(data?.recordingRetentionMonths ?? 12)}
            onChange={v => retentionMutation.mutate(Number(v))}
            options={RETENTION_OPTIONS}
            disabled={retentionMutation.isPending}
          />
        </div>
        <p className="text-xs text-gray-500">Recordings older than this are automatically deleted by a daily cleanup. Default 12 months.</p>
      </div>
    </div>
  )
}

function CompanyGeneralCard() {
  const { companyId } = useParams<{ companyId: string }>()
  const qc = useQueryClient()
  const { user } = useAuthStore()
  const canEdit = user?.role === 'administrator'

  const { data, isLoading } = useQuery({
    queryKey: ['company-general', companyId],
    queryFn: () => companySettingsApi.getGeneral(companyId!).then(r => r.data),
    enabled: !!companyId,
  })

  const { register, handleSubmit, reset, watch, setValue, formState: { isDirty } } = useForm<{
    name: string; website?: string; logoUrl?: string; timezone?: string
  }>()

  // Reset form when company data loads (defaults the inputs).
  useEffect(() => {
    if (data) reset({
      name: data.name ?? '',
      website: data.website ?? '',
      logoUrl: data.logoUrl ?? '',
      timezone: data.timezone || 'Europe/Tirane',
    })
  }, [data, reset])

  const mutation = useMutation({
    mutationFn: (dto: { name: string; website?: string; logoUrl?: string; timezone?: string }) =>
      companySettingsApi.updateGeneral(companyId!, dto),
    onSuccess: () => {
      toast.success('Company settings saved')
      qc.invalidateQueries({ queryKey: ['company-general', companyId] })
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message
      toast.error(typeof msg === 'string' ? msg : 'Failed to save')
    },
  })

  return (
    <div className="card p-4 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-gray-700">Company</p>
        {!canEdit && (
          <span className="inline-flex items-center gap-1 text-xs text-gray-500">
            <Lock className="w-3 h-3" />
            Read-only
          </span>
        )}
      </div>
      {!canEdit && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
          To change these details, contact your administrator.
        </p>
      )}
      {isLoading ? (
        <p className="text-xs text-gray-500">Loading…</p>
      ) : (
        <form onSubmit={handleSubmit(d => mutation.mutate(d))} className="space-y-3">
          <div>
            <label className="label">Company Name *</label>
            <input
              {...register('name', { required: true })}
              className="input"
              disabled={!canEdit}
              readOnly={!canEdit}
            />
          </div>
          <div>
            <label className="label">Website</label>
            <input
              {...register('website')}
              className="input"
              placeholder="https://..."
              disabled={!canEdit}
              readOnly={!canEdit}
            />
          </div>
          <div>
            <label className="label">Logo URL</label>
            <input
              {...register('logoUrl')}
              className="input"
              placeholder="https://..."
              disabled={!canEdit}
              readOnly={!canEdit}
            />
          </div>
          <div>
            <label className="label">Timezone</label>
            <DropdownSelect
              value={watch('timezone') ?? 'Europe/Tirane'}
              onChange={v => setValue('timezone', v, { shouldDirty: true })}
              options={TIMEZONE_OPTIONS}
              disabled={!canEdit}
            />
          </div>
          {canEdit && (
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={!isDirty || mutation.isPending}
                className="btn btn-primary btn-sm"
              >
                {mutation.isPending ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          )}
        </form>
      )}
    </div>
  )
}

function MyAccountCard() {
  const { user } = useAuthStore()
  return (
    <div className="card p-4 space-y-3">
      <p className="text-sm font-medium text-gray-700">My Account</p>
      <div className="space-y-1">
        <p className="text-sm text-gray-900">{formatFullName(user?.firstName, user?.lastName)}</p>
        <p className="text-sm text-gray-500">{user?.email}</p>
        <span className="badge badge-blue text-xs">{user?.role}</span>
      </div>
    </div>
  )
}

function ChangePasswordCard() {
  const { register, handleSubmit, reset } = useForm<{
    currentPassword: string; newPassword: string; confirmPassword: string
  }>()

  const mutation = useMutation({
    mutationFn: (dto: { currentPassword: string; newPassword: string }) =>
      authApi.changePassword(dto),
    onSuccess: () => { reset(); toast.success('Password changed') },
    onError: () => toast.error('Failed to change password'),
  })

  return (
    <div className="card p-4 space-y-3">
      <p className="text-sm font-medium text-gray-700">Change Password</p>
      <form
        onSubmit={handleSubmit(({ currentPassword, newPassword, confirmPassword }) => {
          if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return }
          mutation.mutate({ currentPassword, newPassword })
        })}
        className="space-y-3"
      >
        <div><label className="label">Current Password</label><input {...register('currentPassword', { required: true })} type="password" className="input" /></div>
        <div><label className="label">New Password</label><input {...register('newPassword', { required: true, minLength: 6 })} type="password" className="input" /></div>
        <div><label className="label">Confirm New Password</label><input {...register('confirmPassword', { required: true })} type="password" className="input" /></div>
        <button type="submit" disabled={mutation.isPending} className="btn btn-primary btn-sm">Update Password</button>
      </form>
    </div>
  )
}
